// Template for [JS3Doc](https://jsdoc.app/) documentation auto-generation
// Add / remove sections below as needed
/**
 * DataManagementApi.ts
 *
 * Client methods implementing the quanterix-protos/data_management/v1 API
 */
import { createChannel, createClient } from 'nice-grpc-web';
import * as DataManagement from 'quanterix-protos/data_management/v1/data_management';
import {
  GetAllAssaysRequest,
  GetAllExperimentsRequest,
  GetVersionsRequest,
  WriteExperimentRequest,
  WritePlateRequest,
} from 'quanterix-protos/data_management/v1/data_management_messages';
import { AssayProtocolDto } from 'quanterix-protos/run_data/v1/db_assay';
import {
  PlateBody,
  WellDto,
} from 'quanterix-protos/run_data/v1/db_plate';
import { RunPackageBody, RunPackageDto } from 'quanterix-protos/run_data/v1/db_run_package';
import { PlateWellType } from 'quanterix-protos/type/plate_well_types';
import {
  DataManagementThunkApi,
} from 'quil';

const dataManagementAddress = `/data-manager`;

const dataManagementChannel = createChannel(dataManagementAddress);

export const dataMgr: DataManagement.DataManagementClient = createClient(
  DataManagement.DataManagementDefinition,
  dataManagementChannel,
);

export async function GetVersions() {
  const request = GetVersionsRequest.create({});
  const response = await dataMgr.getVersions(request);
  return response;
}

// TODO: This can be made available as an export from quil
// Or export an empty plate
const flatWellAddressesByCol: string[] = [];
for (let col = 0; col < 12; col += 1) {
  for (let row = 0; row < 8; row += 1) {
    const rowChar = 65 + row;
    const colStr = (col + 1).toString().padStart(2, '0');
    const address = `${String.fromCharCode(rowChar)}${colStr}`;
    flatWellAddressesByCol.push(address);
  }
}

// TODO: Incomplete - error traced to empty Controls set in editor (fix in quil)
/**
 * Create a new empty experiment
 * @param experimentName Name of the new experiment
 * @returns RunPackageDto
 */
export async function createNewExperiment(experimentName: string, refPlateId?: string, inputDataFormatValue?: string) {
  const emptyPlate = flatWellAddressesByCol.map((address) => WellDto.create({wellAddress: address, wellType: PlateWellType.PLATE_WELL_TYPE_EMPTY }));
  const emptyPlateBody: PlateBody = {
    plateName: `${experimentName}_plate`,
    inDraft: false,
    wells: [...emptyPlate],
  }

  let writePlateRequest: WritePlateRequest = {
    body: emptyPlateBody,
  }

  // Clone reference plate if provided
    if (refPlateId){
    const refPlate = await dataMgr.getPlate({plateId: refPlateId});
    const refPlateBody = refPlate?.plate?.data;
    writePlateRequest = {body: refPlateBody}
  }
  
  const plateResponse = await dataMgr.writePlate(writePlateRequest);

  const experimentBody = RunPackageBody.create({
    runPackageName: experimentName,
    locked: false,
    plateId: plateResponse.plate?.plateId,
    analysisConfiguration: {},
    inputDataFormat: inputDataFormatValue
  });

  const writeExperimentRequest: WriteExperimentRequest = {
    body: experimentBody
  }

  const writeExperimentResponse = await dataMgr.writeExperiment(writeExperimentRequest);

  return writeExperimentResponse?.runPackage
}


export const DataManagementApi: DataManagementThunkApi = {
  /**
   * TODO: Temporary fix related to SFSE-2117
   * Here we are awaiting each call to dataMgr.getAssayProtocol to
   * avoid resource starvation in the backend service cluster -
   * the client should not need to worry about backend resource
   * constraints (and has no way of knowing what they are)
   * @returns
   */
  async getAssayList() {
    const assayListRequest = GetAllAssaysRequest.create({});
    const assayListResponse = await dataMgr.getAllAssays(assayListRequest);

    const allAssays: AssayProtocolDto[] = [];
    const assayIds = assayListResponse.assayProtocols.map((e) => e.id);
    // eslint-disable-next-line no-restricted-syntax
    for await (const id of assayIds) {
      // eslint-disable-next-line no-await-in-loop
      const getAssayResponse = await dataMgr.getAssay({ assayProtocolId: id });
      if (getAssayResponse?.protocol) {
        allAssays.push(getAssayResponse.protocol);
      }
    }

    return allAssays || [];
  },
  /**
   * TODO: Temporary fix related to SFSE-2117
   * Here we are awaiting each call to dataMgr.getExperiment to
   * avoid resource starvation in the backend service cluster -
   * the client should not need to worry about backend resource
   * constraints (and has no way of knowing what they are)
   * @returns
   */
  async getExperimentList() {
    const experimentsRequest = GetAllExperimentsRequest.create({});
    const experimentsResponse = await dataMgr.getAllExperiments(experimentsRequest);

    const allExp: RunPackageDto[] = [];
    const expIds = experimentsResponse.experiments.map((e) => e.id);
    // eslint-disable-next-line no-restricted-syntax
    for await (const id of expIds) {
      // eslint-disable-next-line no-await-in-loop
      const getExperimentResponse = await dataMgr.getExperiment({ runId: id });
      if (getExperimentResponse?.experiment) {
        allExp.push(getExperimentResponse.experiment);
      }
    }

    return allExp || [];
  },
  // Requires an updated in DataManagement to leverage
  // IMessage.MergeFrom
  async saveExperiment(experimentData) {
    if (!experimentData.experiment) {
      throw new Error(`Request ${experimentData} is missing information`);
    }

    const updatedData = RunPackageBody.fromPartial({
      ...experimentData.experiment?.data,
    });

    // Initialize an empty plate if one does not exist yet in the experiment
    if (!experimentData.plate) {
      // TODO: this shouldn't be a valid scenario here
      // Review condition and update if necessary
    } else {
      const writePlateRequest: WritePlateRequest = {
        plateId: experimentData.plate.plateId,
        body: experimentData.plate.data,
      };
      await dataMgr.writePlate(writePlateRequest);
    }

    const writeExperimentRequest = WriteExperimentRequest.create({
      runId: experimentData.experiment.runPackageId || undefined,
      body: updatedData,
    });

    await dataMgr.writeExperiment(writeExperimentRequest);
  }
};
